import axios from 'axios'

const API_KEY = process.env.SHIPSTATION_API_KEY
const API_SECRET = process.env.SHIPSTATION_API_SECRET
const BASE_URL = 'https://ssapi.shipstation.com'

// Validate environment variables
if (!API_KEY || !API_SECRET) {
  console.warn(
    'ShipStation API credentials not found. Please set SHIPSTATION_API_KEY and SHIPSTATION_API_SECRET',
  )
}

const authHeader = 'Basic ' + btoa(`${API_KEY}:${API_SECRET}`)

/**
 * Fetches the shipByDate from ShipStation using Shopify order number
 * @param {string|number} orderNumber - The order number from Shopify
 * @returns {Promise<string|null>} - The shipByDate or null if not found
 */
export async function getShipByDate(orderNumber) {
  // Validate input
  if (!orderNumber) {
    console.warn('Order number is required')
    return null
  }

  // Check if credentials are available
  if (!API_KEY || !API_SECRET) {
    console.error('ShipStation API credentials not configured')
    return null
  }

  try {
    // First, search for the order by orderNumber
    const orderResp = await axios.get(
      `${BASE_URL}/orders?orderNumber=${orderNumber}`,
      {
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      },
    )

    const orderId = orderResp.data?.orders?.[0]?.orderId
    if (!orderId) {
      console.warn(`Order ${orderNumber} not found in ShipStation`)
      return null
    }

    // Get detailed order information including shipByDate
    const detailResp = await axios.get(`${BASE_URL}/orders/${orderId}`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 second timeout
    })

    return detailResp.data?.shipByDate
  } catch (err) {
    if (err.response) {
      // Server responded with error status
      console.error(
        `ShipStation API error: ${err.response.status} - ${err.response.data?.message || err.message}`,
      )
    } else if (err.request) {
      // Request was made but no response received
      console.error('ShipStation API request failed - no response received')
    } else {
      // Something else happened
      console.error('Error fetching ShipStation data:', err.message)
    }
    return null
  }
}

/**
 * Helper function to parse ShipStation dates correctly
 * ShipStation returns dates without timezone info, but they are in Pacific Time
 * @param {string} shipStationDate - Date string from ShipStation (e.g., "2025-07-20T22:00:00.0000000")
 * @returns {Date} - Properly parsed Date object
 */
export function parseShipStationDate(shipStationDate) {
  if (!shipStationDate) return null

  try {
    // ShipStation dates are in Pacific Time but don't include timezone info
    // Add Pacific Time offset (-08:00 for PST, -07:00 for PDT)
    // We use -08:00 (PST) as it's more common
    const dateWithTimezone = shipStationDate + '-08:00'
    const date = new Date(dateWithTimezone)

    if (isNaN(date.getTime())) {
      console.warn(
        'Invalid date format received from ShipStation:',
        shipStationDate,
      )
      return null
    }

    return date
  } catch (err) {
    console.error('Error parsing ShipStation date:', err)
    return null
  }
}

/**
 * Formats the shipByDate for display
 * @param {string} shipByDate - ISO date string from ShipStation
 * @returns {string} - Formatted date string
 */
export function formatShipByDate(shipByDate) {
  if (!shipByDate) return null

  try {
    const date = parseShipStationDate(shipByDate)

    if (!date) {
      return shipByDate
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Chicago', // Convert to Central Time
    })
  } catch (err) {
    console.error('Error formatting date:', err)
    return shipByDate
  }
}

/**
 * Get order details from ShipStation
 * @param {string|number} orderNumber - The order number from Shopify
 * @returns {Promise<Object|null>} - Full order details or null if not found
 */
export async function getOrderDetails(orderNumber) {
  // Validate input
  if (!orderNumber) {
    console.warn('Order number is required')
    return null
  }

  // Check if credentials are available
  if (!API_KEY || !API_SECRET) {
    console.error('ShipStation API credentials not configured')
    return null
  }

  try {
    // First, search for the order by orderNumber
    const orderResp = await axios.get(
      `${BASE_URL}/orders?orderNumber=${orderNumber}`,
      {
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      },
    )

    const orderId = orderResp.data?.orders?.[0]?.orderId
    if (!orderId) {
      console.warn(`Order ${orderNumber} not found in ShipStation`)
      return null
    }

    // Get detailed order information
    const detailResp = await axios.get(`${BASE_URL}/orders/${orderId}`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    })

    return detailResp.data
  } catch (err) {
    if (err.response) {
      console.error(
        `ShipStation API error: ${err.response.status} - ${err.response.data?.message || err.message}`,
      )
    } else if (err.request) {
      console.error('ShipStation API request failed - no response received')
    } else {
      console.error('Error fetching ShipStation order details:', err.message)
    }
    return null
  }
}

/**
 * Fetches all tags from ShipStation account
 * @returns {Promise<Array|null>} - Array of tags or null if error
 */
export async function getTags() {
  // Check if credentials are available
  if (!API_KEY || !API_SECRET) {
    console.error('ShipStation API credentials not configured')
    return null
  }

  try {
    const response = await axios.get(`${BASE_URL}/accounts/listtags`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 second timeout
    })

    return response.data || []
  } catch (err) {
    if (err.response) {
      console.error(
        `ShipStation API error fetching tags: ${err.response.status} - ${err.response.data?.message || err.message}`,
      )
    } else if (err.request) {
      console.error(
        'ShipStation API request failed fetching tags - no response received',
      )
    } else {
      console.error('Error fetching ShipStation tags:', err.message)
    }
    return null
  }
}

/**
 * Get order details from ShipStation including tagIds
 * @param {string|number} orderNumber - The order number from Shopify
 * @returns {Promise<Object|null>} - Full order details including tagIds or null if not found
 */
export async function getOrderDetailsWithTags(orderNumber) {
  // Validate input
  if (!orderNumber) {
    console.log('Order number is required for getOrderDetailsWithTags')
    return null
  }

  try {
    // First, search for the order by orderNumber
    const orderResp = await axios.get(
      `${BASE_URL}/orders?orderNumber=${orderNumber}`,
      {
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      },
    )

    // use orderResp.data?.orders to filter only "orderStatus": "awaiting_shipment" and after that get the closer "shipByDate" in the future
    const awaitingShipmentOrders = orderResp.data?.orders?.filter(order => order.orderStatus === 'awaiting_shipment')

    // look for the order with the closest "shipByDate" in the future
    const orderId = awaitingShipmentOrders?.sort((a, b) => new Date(a.shipByDate) - new Date(b.shipByDate))[0]?.orderId

    if (!orderId) {
      console.warn(`Order ${orderNumber} not found in ShipStation`)
      return null
    }

    // Get detailed order information including tagIds
    const detailResp = await axios.get(`${BASE_URL}/orders/${orderId}`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    })

    return detailResp.data
  } catch (err) {
    if (err.response) {
      console.error(
        `ShipStation API error fetching order details with tags: ${err.response.status} - ${err.response.data?.message || err.message}`,
      )
    } else if (err.request) {
      console.error(
        'ShipStation API request failed fetching order details with tags - no response received',
      )
    } else {
      console.error(
        'Error fetching ShipStation order details with tags:',
        err.message,
      )
    }
    return null
  }
}

/**
 * Calculate delivery date based on order tags, account tags, and ship date
 * @param {Array} orderTags - Array of tagIds from the order
 * @param {Array} tags - Array of tag objects from the account
 * @param {Date|string} shipDate - The ship by date
 * @returns {Date} - Calculated delivery date
 */
export function getDeliveryDate(orderTags, tags, shipDate) {
  if (!orderTags || !tags || !shipDate) {
    console.warn(
      'Missing required parameters for delivery date calculation. Using shipDate as fallback.',
    )
    return parseShipStationDate(shipDate)
  }

  // Find transit tag that matches the pattern "X day Transit"
  const transitTag = tags.find(
    (tag) =>
      orderTags.includes(tag.tagId) && /(\d+)\s*day\s*Transit/i.test(tag.name),
  )

  if (!transitTag) {
    console.warn('No transit tag found. Using shipDate as deliveryDate.')
    return parseShipStationDate(shipDate)
  }

  // Extract transit days from tag name
  const match = transitTag.name.match(/(\d+)\s*day\s*Transit/i)
  const transitDays = match ? parseInt(match[1], 10) : 0

  if (transitDays === 0) {
    console.warn('Transit days is 0. Using shipDate as deliveryDate.')
    return parseShipStationDate(shipDate)
  }

  // Calculate delivery date by adding transit days to ship date
  const ship = parseShipStationDate(shipDate)
  ship.setDate(ship.getDate() + transitDays)

  return ship
}

/**
 * Calculate delivery date for a specific order number
 * @param {string|number} orderNumber - The order number from Shopify
 * @returns {Promise<Date|null>} - Calculated delivery date or null if error
 */
export async function calculateDeliveryDate(orderNumber) {
  try {
    // Get order details with tags
    const orderDetails = await getOrderDetailsWithTags(orderNumber)
    if (!orderDetails) {
      console.log(`Unable to get order details for ${orderNumber}`)
      return null
    }

    // Get account tags
    const tags = await getTags()
    if (!tags) {
      console.warn('Unable to get account tags')
      // Fallback to ship date if we can't get tags
      return orderDetails.shipByDate
        ? parseShipStationDate(orderDetails.shipByDate)
        : null
    }

    // Calculate delivery date
    const deliveryDate = getDeliveryDate(
      orderDetails.tagIds || [],
      tags,
      orderDetails.shipByDate,
    )

    return deliveryDate
  } catch (err) {
    console.error('Error calculating delivery date:', err.message)
    return null
  }
}

/**
 * Formats the delivery date for display
 * @param {Date|string} deliveryDate - Date object or ISO date string
 * @returns {string|null} - Formatted date string
 */
export function formatDeliveryDate(deliveryDate) {
  if (!deliveryDate) return null

  try {
    const date = new Date(deliveryDate)

    if (isNaN(date.getTime())) {
      console.warn('Invalid delivery date format received:', deliveryDate)
      return null
    }

    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })

    return formattedDate
  } catch (err) {
    console.error('Error formatting delivery date:', err)
    return deliveryDate.toString()
  }
}

/**
 * Get unfulfilled orders for a specific customer from ShipStation
 * @param {string} customerEmail - Customer email address
 * @param {string} customerName - Customer name for ShipStation filtering
 * @returns {Promise<Object|null>} - Most recent unfulfilled order with delivery date or null
 */
export async function getUnfulfilledOrdersForCustomer(
  customerEmail,
  customerName,
) {
  try {
    const response = await fetch('/api/shipstation/next-box-date', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerEmail,
        customerName,
      }),
    })

    const data = await response.json()

    return data
  } catch (error) {
    console.error('Error fetching unfulfilled orders:', error)
    return null
  }
}
